import React, { useState, useMemo, useEffect, useRef } from 'react'
import { Controller, FieldValues, Path } from 'react-hook-form'

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Icon,
  Select,
  Switch,
  Textfield,
  ButtonTabs
} from '@clientbase/clientbase-library'
import {
  Box,
  Checkbox,
  FormControlLabel,
  GlobalStyles,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  Tooltip,
  Typography
} from '@mui/material'
import { useAuthContext } from 'context/AuthContext'
import moment from 'moment'
import {
  IssueBillingType,
  IssueBillingTypeLabels
} from 'templates/Settings/Body/Sections/Notifications/context/Notifications.interface'

import { useSessionCookies } from 'components/CompanySelector'
import NumberFormatCustom from 'components/PorcentageInput/PorcentageInput'
import Upgrade from 'components/Upgrade'

import { onlyNumber } from 'utils'

import { theme } from 'styles/theme'

import { expirationOptions } from '../Discounts/Discounts.utils'
import { AdvancedOptionsProps } from './AdvancedOptions.interface'

// Cycle options for bonification
export const bonificationCyclesOptions = [
  { label: '1', value: 1 },
  { label: '2', value: 2 },
  { label: '3', value: 3 },
  { label: '4', value: 4 },
  { label: '5', value: 5 },
  { label: '6', value: 6 },
  { label: '7', value: 7 },
  { label: '8', value: 8 },
  { label: '9', value: 9 },
  { label: '10', value: 10 },
  { label: '11', value: 11 },
  { label: '12', value: 12 },
  { label: 'Outros', value: 13 }
]

export const BonusStatus = {
  no_bonus: 'no_bonus',
  fixed: 'fixed',
  percentage: 'percentage'
} as const

/**
 * Calcula os dias de expiração baseado no tipo de contexto
 * - Para charges: calcula diferença entre expirationField (data) e dueDate
 * - Para recorrências/links/planos: usa diretamente o expirationField que já contém os dias
 */
const calculateExpirationDays = (
  editData: any,
  isCharge: boolean,
  isProductOrLink = false
): number => {
  // Para links de contratação e planos, sempre usar o número de dias diretamente
  if (isProductOrLink || !isCharge) {
    return (
      Number(editData?.expirationField) ||
      Number(editData?.limit_days_for_payment) ||
      60
    )
  }

  // Para charges, calcula a diferença entre expirationField (data de expiração) e dueDate
  if (editData?.expirationField && editData?.dueDate) {
    try {
      const expirationDate = moment(editData.expirationField)
      const dueDateMoment = moment(editData.dueDate)

      // Verifica se as datas são válidas
      if (!expirationDate.isValid() || !dueDateMoment.isValid()) {
        return 60
      }

      const daysDiff = expirationDate.diff(dueDateMoment, 'days')
      return daysDiff > 0 ? daysDiff : 60
    } catch (error) {
      return 60
    }
  }

  // Se não tiver dados suficientes, usa o padrão de 60 dias
  return 60
}

const AdvancedOptions = <T extends FieldValues>({
  control,
  watch,
  setValue,
  trigger,
  clearErrors,
  isCharge = false,
  isChargeOrRecurrenceForm = false,
  limit_days_for_payment,
  editData,
  isProductOrLink = false
}: AdvancedOptionsProps<T>) => {
  const { capabilities } = useAuthContext()
  const bonusAccess = capabilities?.['billing.bonus']
  const { ['nextauth.days_to_issue_billing']: days_to_issue_billing } =
    useSessionCookies()
  const [expandedAdvancedOptions, setExpandedAdvancedOptions] = useState(false)
  const [expandedAccordion2, setExpandedAccordion2] = useState(true)
  const [isBonifyAll, setIsBonifyAll] = useState(false)
  const previousBonusState = useRef<boolean | null>(null)

  const hasBonus = watch('hasBonus' as Path<T>)
  const hasIssueDays = watch('hasIssueDays' as Path<T>)
  const issueBillingType = watch('issueBillingType' as Path<T>)

  // Get the current totalCycles value from the form
  const totalCycles = watch('totalCycles' as Path<T>)
  const totalCyclesCustom = watch('totalCyclesCustom' as Path<T>)

  // The recurrence's own cycle count uses 13 as an "Outros" sentinel, storing the
  // real number in totalCyclesCustom (e.g. a 31-cycle recurrence has
  // totalCycles === 13 and totalCyclesCustom === '31'). Resolve the real value so
  // the bonification options/bounds reflect the actual number of cycles.
  const effectiveTotalCycles =
    totalCycles === 13 ? Number(totalCyclesCustom) || 0 : totalCycles

  // The bonus only covers cycles from startCycle onward, so the maximum number
  // of bonifiable cycles is what remains from startCycle to the last cycle
  // (e.g. starting at cycle 2 in a 31-cycle recurrence allows at most 30).
  const startCycle = Number(watch('bonusSettings.startCycle' as Path<T>)) || 1
  const maxBonusCycles =
    effectiveTotalCycles === 0
      ? 0 // infinite recurrence: no finite cap
      : Math.max(effectiveTotalCycles - startCycle + 1, 0)

  // Filter bonification cycles options based on the cycles remaining from startCycle
  const filteredBonificationCyclesOptions = useMemo(() => {
    if (!isChargeOrRecurrenceForm) {
      return bonificationCyclesOptions.filter((option) => option.value !== 13)
    }

    // If totalCycles is 0 (infinite), show all options including "Outros"
    if (effectiveTotalCycles === 0) {
      return bonificationCyclesOptions
    }

    // If totalCycles is 1 (unique charge), don't show any options
    // EXCEPT for products/links which are templates and can have bonification even when unique
    if (effectiveTotalCycles === 1 && !isProductOrLink) {
      return []
    }

    // When more than 12 cycles remain from startCycle, keep "Outros" so the user
    // can enter a custom count (bounded by the remaining cycles)
    if (maxBonusCycles > 12) {
      return bonificationCyclesOptions
    }

    // Otherwise filter options to be <= the cycles remaining from startCycle
    // Only include "Outros" option (value 13) for infinite cycles or > 12 remaining
    return bonificationCyclesOptions.filter(
      (option) => option.value <= maxBonusCycles && option.value !== 13
    )
  }, [
    effectiveTotalCycles,
    maxBonusCycles,
    isChargeOrRecurrenceForm,
    isProductOrLink
  ])

  // Get the current cyclesCount value
  const cyclesCount = watch('bonusSettings.cyclesCount' as Path<T>)

  // Adjust cyclesCount when totalCycles changes
  useEffect(() => {
    // Skip if not in a charge/recurrence form
    if (!isChargeOrRecurrenceForm) return

    // Skip if no bonus is set
    if (!hasBonus) return

    // Skip if cyclesCount is not set yet
    if (!cyclesCount) return

    // When more than 12 cycles remain from startCycle, "Outros" (13) is a valid
    // choice (custom count up to the remaining cycles) — keep it instead of resetting
    if (maxBonusCycles > 12 && cyclesCount === 13) return

    // If totalCycles is not 0 (not infinite) and cyclesCount is 13 (Outros),
    // we need to reset it to the maximum available option
    if (effectiveTotalCycles !== 0 && cyclesCount === 13) {
      // Find the maximum available option within the remaining cycles
      const maxOption = filteredBonificationCyclesOptions.reduce(
        (max, option) =>
          option.value > max && option.value <= maxBonusCycles
            ? option.value
            : max,
        0
      )

      if (maxOption > 0) {
        setValue('bonusSettings.cyclesCount' as any, maxOption as any)
      }
      return
    }

    // Skip if totalCycles is 0 (infinite)
    if (effectiveTotalCycles === 0) return

    // If cyclesCount exceeds the cycles remaining from startCycle, adjust it down
    if (cyclesCount > maxBonusCycles) {
      // Find the maximum available option within the remaining cycles
      const maxOption = filteredBonificationCyclesOptions.reduce(
        (max, option) =>
          option.value > max && option.value <= maxBonusCycles
            ? option.value
            : max,
        0
      )

      if (maxOption > 0) {
        setValue('bonusSettings.cyclesCount' as any, maxOption as any)
      }
    }
  }, [
    effectiveTotalCycles,
    maxBonusCycles,
    cyclesCount,
    hasBonus,
    isChargeOrRecurrenceForm,
    filteredBonificationCyclesOptions
  ])

  const handleAccordion1Change = (
    _: React.ChangeEvent<object>,
    isExpanded: boolean
  ) => {
    // Don't allow any changes if it's a charge (except for products/links which are templates) or if user doesn't have access
    if ((isCharge && !isProductOrLink) || !bonusAccess?.has_access) {
      return
    }

    setValue('hasBonus' as any, isExpanded as any, { shouldValidate: false })

    // If turning on bonification, ensure bonusSettings has default values
    if (isExpanded) {
      const currentBonusSettings = watch('bonusSettings' as Path<T>)

      if (!currentBonusSettings || !currentBonusSettings.type) {
        setValue(
          'bonusSettings' as any,
          {
            cyclesCount: 1,
            type: 'value',
            amount: '',
            cyclesCountCustom: ''
          } as any,
          { shouldValidate: false }
        )
      }
      // Don't trigger validation when opening - let user interact first
    } else {
      // If turning off bonification, clear all bonus-related errors
      clearErrors('bonusSettings.amount' as any)
      clearErrors('bonusSettings.type' as any)
      clearErrors('bonusSettings.cyclesCount' as any)
      clearErrors('bonusSettings.cyclesCountCustom' as any)
      clearErrors('bonusSettings' as any)

      // Trigger validation to update isValid state
      setTimeout(() => {
        trigger('bonusSettings' as any)
      }, 0)
    }
  }

  const handleAccordion2Change = (
    _: React.ChangeEvent<object>,
    isExpanded: boolean
  ) => {
    setExpandedAccordion2(isExpanded)
  }
  const handleAccordion3Change = (
    _: React.ChangeEvent<object>,
    isExpanded: boolean
  ) => {
    setValue('hasIssueDays' as any, isExpanded as any)
    setTimeout(() => {
      trigger('issueDays' as any)
    }, 100)
  }

  // Efeito para controlar a bonificação com base no isCharge
  useEffect(() => {
    // Se não estiver em um formulário de cobrança/recorrência, não faz nada
    // IMPORTANTE: Produtos e links são templates e podem ter bonificação mesmo sendo únicos
    if (!isChargeOrRecurrenceForm || isProductOrLink) return

    // Obtém o valor atual de hasBonus
    const currentHasBonus = watch('hasBonus' as Path<T>)

    if (isCharge) {
      // Se mudou para cobrança e a bonificação está ativa
      if (currentHasBonus) {
        // Salva o estado atual antes de desativar
        previousBonusState.current = true
        // Desativa a bonificação
        setValue('hasBonus' as any, false as any)
      }
    } else {
      // Se mudou para recorrência e havia um estado anterior salvo
      if (previousBonusState.current === true) {
        // Restaura o estado anterior da bonificação
        setValue('hasBonus' as any, true as any)
        // Limpa o estado salvo após restaurar
        previousBonusState.current = null
      }
    }
  }, [isCharge, isChargeOrRecurrenceForm, isProductOrLink, setValue, watch])

  // Efeito para salvar o estado atual da bonificação quando o usuário a ativa manualmente
  useEffect(() => {
    // Se não estiver em um formulário de cobrança/recorrência, não faz nada
    // IMPORTANTE: Produtos e links são templates e podem ter bonificação mesmo sendo únicos
    if (!isChargeOrRecurrenceForm || isProductOrLink) return

    // Obtém o valor atual de hasBonus
    const hasBonus = watch('hasBonus' as Path<T>)

    // Se não for cobrança e a bonificação estiver ativa, salva o estado
    if (!isCharge && hasBonus) {
      previousBonusState.current = true
    }
  }, [isCharge, isChargeOrRecurrenceForm, isProductOrLink, watch])

  // Initialize expanded state based on hasBonus value
  useEffect(() => {
    const currentHasBonus = watch('hasBonus' as Path<T>)
    if (currentHasBonus) {
      setExpandedAdvancedOptions(true)
    } else {
      setExpandedAdvancedOptions(false)
    }
  }, [watch])

  // Update expanded state when hasBonus or hasIssueDays changes
  useEffect(() => {
    // Abre as opções avançadas quando bonificação ou dias de envio são ativados
    // Não fecha automaticamente quando são desativados
    if (hasBonus || hasIssueDays) {
      setExpandedAdvancedOptions(true)
    }
  }, [hasBonus, hasIssueDays])

  // Calcular e setar o valor correto de expiração na edição
  useEffect(() => {
    if (!editData) return

    // Para links/planos ou recorrências, sempre usar o número de dias diretamente
    if (isProductOrLink || !isCharge) {
      const expirationDays = calculateExpirationDays(
        editData,
        isCharge,
        isProductOrLink
      )
      const currentValue = watch('expirationField' as Path<T>)

      if (currentValue !== expirationDays.toString()) {
        setTimeout(() => {
          setValue('expirationField' as any, expirationDays.toString() as any)
        }, 100)
      }
    }
    // Para charges (cobranças únicas), calcular diferença entre datas
    else if (isCharge && editData.expirationField && editData.dueDate) {
      const expirationDays = calculateExpirationDays(
        editData,
        isCharge,
        isProductOrLink
      )
      const currentValue = watch('expirationField' as Path<T>)

      if (currentValue !== expirationDays.toString()) {
        setTimeout(() => {
          setValue('expirationField' as any, expirationDays.toString() as any)
        }, 100)
      }
    }
  }, [
    editData,
    isCharge,
    isChargeOrRecurrenceForm,
    isProductOrLink,
    setValue,
    watch
  ])

  // Sync isBonifyAll checkbox when editing existing data
  const bonusType = watch('bonusSettings.type' as Path<T>)
  const bonusAmount = watch('bonusSettings.amount' as Path<T>)

  useEffect(() => {
    if (!hasBonus) return

    const amountStr = String(bonusAmount || '')
    const percentageValue = parseFloat(amountStr.replace(',', '.'))

    if (bonusType === 'percentage' && percentageValue >= 100) {
      setIsBonifyAll(true)
    }
  }, [hasBonus, bonusType, bonusAmount])

  // Reset isBonifyAll when bonus is turned off
  useEffect(() => {
    if (!hasBonus) {
      setIsBonifyAll(false)
    }
  }, [hasBonus])

  return (
    <Stack mt={3} width="100%">
      <Stack
        data-cy="advanced-options-toggle"
        onClick={() => setExpandedAdvancedOptions(!expandedAdvancedOptions)}
        direction="row"
        gap={0.5}
        alignItems="center"
        mb={2}
        sx={{ cursor: 'pointer' }}
      >
        <Typography variant="mdBold">Opções avançadas</Typography>

        <Button
          startIcon={expandedAdvancedOptions ? 'faChevronUp' : 'faChevronDown'}
          variantButton="ghost"
          sx={{
            fontSize: '13px',
            width: '24px',
            height: '24px',
            ':hover': {
              height: '24px'
            }
          }}
        />
      </Stack>

      {expandedAdvancedOptions && (
        <Stack gap={1.5}>
          <Tooltip
            enterTouchDelay={0}
            arrow
            placement="top"
            title={
              isCharge && !isProductOrLink
                ? 'A bonificação está disponível somente em recorrências'
                : ''
            }
            disableHoverListener={!isCharge || isProductOrLink}
          >
            {/* Box wrapper garante que o tooltip apareça mesmo com o Accordion desabilitado (elementos disabled não disparam eventos de hover no MUI) */}
            <Box>
              <Accordion
                expanded={Boolean(hasBonus)}
                onChange={handleAccordion1Change}
                disabled={isCharge && !isProductOrLink}
                sx={{
                  border: '1px solid #E8EBE8',
                  borderLeft: `3px solid ${theme.palette.primary.main}`,
                  borderRadius: '8px !important',
                  backgroundColor: '#FAFBFA',
                  boxShadow: 'none',
                  '&:before': { display: 'none' },
                  '&.Mui-expanded': {
                    margin: 0,
                    backgroundColor: '#fff'
                  },
                  '&.Mui-disabled': {
                    backgroundColor: '#FAFBFA !important'
                  },
                  position: 'relative'
                }}
              >
                <AccordionSummary
                  sx={{
                    '.MuiAccordionSummary-content': { margin: '12px 0px' }
                  }}
                  hasSwitch
                  expandIcon={
                    <Switch
                      disabled={!bonusAccess?.has_access}
                      checked={Boolean(hasBonus)}
                    />
                  }
                >
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Typography variant="smBold">Bonificação</Typography>
                    <Tooltip
                      enterTouchDelay={0}
                      arrow
                      placement="top"
                      title="Ofereça uma bonificação em ciclos específicos da recorrência"
                    >
                      <Box>
                        <Icon
                          icon="info"
                          sx={{
                            fontSize: '1.2rem',
                            mt: '3px',
                            color: theme.palette.neutral[600]
                          }}
                        />
                      </Box>
                    </Tooltip>
                    {!bonusAccess?.has_access && (
                      <Upgrade
                        resourceName="Bonificação"
                        plan={bonusAccess?.plan}
                      />
                    )}
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack gap={1.5}>
                    {/* Start Cycle and Cycles Count on same line (50%/50%) - matching Discounts order */}
                    <Grid container spacing={2}>
                      {/* A partir de qual ciclo? */}
                      <Grid item xs={12} sm={6}>
                        <Box
                          mb={1}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5
                          }}
                        >
                          <Typography variant="smLight">
                            A partir de qual ciclo?
                          </Typography>
                          <Tooltip
                            enterTouchDelay={0}
                            arrow
                            placement="top"
                            title="A partir de qual ciclo da recorrência a bonificação começará a valer."
                          >
                            <Box
                              component="span"
                              sx={{ cursor: 'help', display: 'flex' }}
                            >
                              <Icon
                                icon="info"
                                sx={{
                                  fontSize: '14px',
                                  color: theme.palette.neutral[500]
                                }}
                              />
                            </Box>
                          </Tooltip>
                        </Box>
                        <Controller
                          control={control}
                          name={'bonusSettings.startCycle' as Path<T>}
                          render={({ field, fieldState: { error } }) => (
                            <Textfield
                              id="bonus-start-cycle-input"
                              type="number"
                              inputProps={{ min: 1 }}
                              placeholder="1"
                              helperText={error?.message}
                              error={!!error?.message}
                              value={field.value || 1}
                              onChange={(e) => {
                                const value = parseInt(e.target.value, 10)
                                if (value >= 1 || e.target.value === '') {
                                  field.onChange(value || 1)
                                }
                              }}
                              sx={{
                                '& .MuiInputBase-input': {
                                  fontSize: '14px'
                                }
                              }}
                            />
                          )}
                        />
                      </Grid>

                      {/* Quantos ciclos você deseja bonificar? */}
                      <Grid item xs={12} sm={6}>
                        <Box
                          mb={1}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5
                          }}
                        >
                          <Typography variant="smLight">
                            Quantos ciclos deseja bonificar?
                          </Typography>
                          <Tooltip
                            enterTouchDelay={0}
                            arrow
                            placement="top"
                            title="Por quantos ciclos será aplicada a bonificação."
                          >
                            <Box
                              component="span"
                              sx={{ cursor: 'help', display: 'flex' }}
                            >
                              <Icon
                                icon="info"
                                sx={{
                                  fontSize: '14px',
                                  color: theme.palette.neutral[500]
                                }}
                              />
                            </Box>
                          </Tooltip>
                        </Box>
                        <Grid container spacing={1} alignItems="flex-start">
                          <Grid item xs={cyclesCount === 13 ? 6 : 12}>
                            <Controller
                              control={control}
                              name={'bonusSettings.cyclesCount' as Path<T>}
                              render={({ field, fieldState: { error } }) => (
                                <Select
                                  id="bonus-cycles-select"
                                  placeholder="Selecione"
                                  helperText={error?.message}
                                  error={!!error?.message}
                                  fullWidth
                                  value={field.value || ''}
                                  onChange={(event) => {
                                    field.onChange(event)

                                    // If "Outros" is selected, initialize the custom cycles count
                                    if (event.target.value === 13) {
                                      setValue(
                                        'bonusSettings.cyclesCountCustom' as any,
                                        '' as any
                                      )
                                    }
                                  }}
                                >
                                  {filteredBonificationCyclesOptions.map(
                                    (option) => (
                                      <MenuItem
                                        key={option.value}
                                        value={option.value}
                                      >
                                        {option.label}
                                      </MenuItem>
                                    )
                                  )}
                                </Select>
                              )}
                            />
                          </Grid>

                          {/* Custom Cycles Input (when "Outros" is selected) */}
                          {cyclesCount === 13 && (
                            <Grid item xs={6}>
                              <Controller
                                control={control}
                                name={
                                  'bonusSettings.cyclesCountCustom' as Path<T>
                                }
                                render={({ field, fieldState: { error } }) => (
                                  <Textfield
                                    helperText={error?.message}
                                    onChange={(event) => {
                                      const res = onlyNumber(event.target.value)
                                      // Allow free typing (up to 3 digits); the
                                      // precise range (13..totalCycles, or 13..120
                                      // for infinite recurrences) is enforced by
                                      // validation with a clear message, instead of
                                      // silently swallowing keystrokes
                                      if (res === '' || res.length <= 3) {
                                        field.onChange(res)
                                      }
                                    }}
                                    value={field.value || ''}
                                    placeholder="Qtd."
                                    error={!!error?.message}
                                    sx={{
                                      '& .MuiInputBase-input': {
                                        fontSize: '14px'
                                      }
                                    }}
                                  />
                                )}
                              />
                            </Grid>
                          )}
                        </Grid>
                      </Grid>
                    </Grid>

                    {/* Type Selection */}
                    <Box
                      sx={{
                        ...(isBonifyAll && {
                          pointerEvents: 'none',
                          opacity: 0.5
                        })
                      }}
                    >
                      <Controller
                        control={control}
                        name={'bonusSettings.type' as Path<T>}
                        render={({ field }) => (
                          <ButtonTabs
                            value={field.value || 'value'}
                            onChange={(_, newValue) => {
                              field.onChange(newValue)

                              // Clear amount field when changing type instead of converting
                              const currentAmount = watch(
                                'bonusSettings.amount' as Path<T>
                              )
                              if (currentAmount && field.value !== newValue) {
                                // Zero out the amount field when switching between value and percentage
                                setValue(
                                  'bonusSettings.amount' as any,
                                  '' as any
                                )
                              }
                            }}
                            tabs={[
                              {
                                label: 'Valor',
                                value: 'value'
                              },
                              {
                                label: 'Porcentagem',
                                value: 'percentage'
                              }
                            ]}
                          />
                        )}
                      />
                    </Box>

                    {/* Amount Input */}
                    <Controller
                      control={control}
                      name={'bonusSettings.amount' as Path<T>}
                      render={({ field, fieldState: { error } }) => {
                        const isPercentage =
                          watch('bonusSettings.type' as Path<T>) ===
                          'percentage'

                        return (
                          <Textfield
                            id="bonus-amount-input"
                            placeholder={isPercentage ? '%' : 'R$'}
                            helperText={error?.message}
                            error={!!error?.message}
                            disabled={isBonifyAll}
                            sx={{
                              '& .MuiInputBase-input': {
                                fontSize: '14px'
                              }
                            }}
                            {...(isPercentage
                              ? {
                                  InputProps: {
                                    inputComponent: NumberFormatCustom as any,
                                    style: { fontSize: '14px' }
                                  }
                                }
                              : { mask: 'money' })}
                            value={field.value || ''}
                            onChange={(e) => {
                              field.onChange(e)
                            }}
                          />
                        )
                      }}
                    />

                    {/* Bonify entire invoice checkbox */}
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isBonifyAll}
                          onChange={(e) => {
                            const checked = e.target.checked
                            setIsBonifyAll(checked)

                            if (checked) {
                              setValue(
                                'bonusSettings.type' as any,
                                'percentage' as any
                              )
                              setValue(
                                'bonusSettings.amount' as any,
                                '100' as any
                              )
                            } else {
                              setValue('bonusSettings.amount' as any, '' as any)
                            }
                          }}
                          size="small"
                        />
                      }
                      label="Bonificar o valor total da fatura"
                      sx={{
                        '& .MuiFormControlLabel-label': {
                          fontSize: '14px'
                        }
                      }}
                    />
                  </Stack>
                </AccordionDetails>
              </Accordion>
            </Box>
          </Tooltip>

          {isChargeOrRecurrenceForm && (
            <Accordion
              expanded={expandedAccordion2}
              onChange={handleAccordion2Change}
              sx={{
                border: '1px solid #E8EBE8',
                borderLeft: `3px solid ${theme.palette.primary.main}`,
                borderRadius: '8px !important',
                backgroundColor: '#FAFBFA',
                boxShadow: 'none',
                '&:before': { display: 'none' },
                '&.Mui-expanded': {
                  margin: 0,
                  backgroundColor: '#fff'
                }
              }}
            >
              <AccordionSummary
                sx={{ '.MuiAccordionSummary-content': { margin: '12px 0px' } }}
              >
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Typography variant="smBold">
                    Expiração da cobrança
                  </Typography>
                  <Tooltip
                    enterTouchDelay={0}
                    arrow
                    placement="right"
                    title={
                      <div>
                        <span>
                          Defina por quantos dias após o vencimento a fatura
                          continuará disponível para pagamento.
                        </span>
                        <br />
                        <br />
                        <span>
                          Por conta da limitação de alguns meios de pagamento, o
                          prazo máximo é de 60 dias.
                        </span>
                      </div>
                    }
                  >
                    <Box>
                      <Icon
                        icon="info"
                        sx={{
                          fontSize: '1.2rem',
                          mt: '3px',
                          color: theme.palette.neutral[600]
                        }}
                      />
                    </Box>
                  </Tooltip>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Controller
                  control={control}
                  name={'expirationField' as Path<T>}
                  render={({
                    fieldState: { error },
                    field: { onChange, value, ref }
                  }) => (
                    <>
                      <GlobalStyles
                        styles={{
                          '.MuiPopover-root:has(.expiration-day-item) .MuiPaper-root':
                            {
                              maxHeight: '280px !important',
                              overflowY: 'auto !important'
                            }
                        }}
                      />
                      <Select
                        inputRef={ref}
                        helperText={error?.message}
                        error={!!error?.message}
                        fullWidth
                        name="expirationField"
                        value={
                          value
                            ? parseInt(value.toString())
                            : parseInt(limit_days_for_payment || '60')
                        }
                        displayEmpty
                        onChange={onChange}
                      >
                        {expirationOptions.map((item) => (
                          <MenuItem
                            className="expiration-day-item"
                            key={item.label}
                            value={item.value}
                          >
                            {item.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </>
                  )}
                />
              </AccordionDetails>
            </Accordion>
          )}
          <Accordion
            expanded={Boolean(hasIssueDays)}
            onChange={handleAccordion3Change}
            sx={{
              border: '1px solid #E8EBE8',
              borderLeft: `3px solid ${theme.palette.primary.main}`,
              borderRadius: '8px !important',
              backgroundColor: '#FAFBFA',
              boxShadow: 'none',
              '&:before': { display: 'none' },
              '&.Mui-expanded': {
                margin: 0,
                backgroundColor: '#fff'
              }
            }}
          >
            <AccordionSummary
              sx={{ '.MuiAccordionSummary-content': { margin: '12px 0px' } }}
            >
              <Box display="flex" alignItems="center" gap={0.5}>
                <Typography variant="smBold">Emissão da cobrança</Typography>
                <Tooltip
                  enterTouchDelay={0}
                  arrow
                  placement="right"
                  title="Defina quando a fatura será emitida e disponibilizada para pagamento."
                >
                  <Box>
                    <Icon
                      icon="info"
                      sx={{
                        fontSize: '1.2rem',
                        mt: '3px',
                        color: theme.palette.neutral[600]
                      }}
                    />
                  </Box>
                </Tooltip>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Stack gap={2}>
                <Controller
                  control={control}
                  name={'issueBillingType' as Path<T>}
                  render={({ field }) => (
                    <RadioGroup
                      row
                      value={field.value || 'days_before_due_date'}
                      onChange={(event) => {
                        field.onChange(event.target.value)
                      }}
                      sx={{ gap: 2 }}
                    >
                      {(
                        Object.keys(
                          IssueBillingTypeLabels
                        ) as IssueBillingType[]
                      ).map((typeKey) => (
                        <FormControlLabel
                          key={typeKey}
                          value={typeKey}
                          control={<Radio size="small" />}
                          label={IssueBillingTypeLabels[typeKey]}
                        />
                      ))}
                    </RadioGroup>
                  )}
                />

                {issueBillingType === 'specific_day' ? (
                  <Box
                    display="flex"
                    gap="10px"
                    flexDirection="row"
                    alignItems="center"
                  >
                    <Typography variant="smLight">Dia</Typography>
                    <Box maxWidth="66px">
                      <Controller
                        control={control}
                        name={'specificDayOfMonth' as Path<T>}
                        render={({ field: { onChange, value } }) => (
                          <Textfield
                            placeholder="1"
                            value={value || ''}
                            onChange={(e) => {
                              const rawValue = e.target.value.replace(/\D/g, '')
                              if (rawValue === '') {
                                onChange(0)
                              } else {
                                const numValue = Math.min(Number(rawValue), 31)
                                onChange(numValue)
                              }
                            }}
                            onBlur={() => {
                              if (!value || Number(value) < 1) {
                                onChange(1)
                              }
                            }}
                          />
                        )}
                      />
                    </Box>
                    <Typography variant="smLight">do mês</Typography>
                    <Tooltip
                      enterTouchDelay={0}
                      arrow
                      placement="right"
                      title="Se o dia escolhido não existir no mês (ex: dia 31 em meses com 30 dias), a fatura será emitida no último dia do mês."
                    >
                      <div>
                        <Icon
                          icon="info"
                          colorSX={theme.palette.neutral[700]}
                          sx={{
                            color: theme.palette.neutral[700],
                            width: '20px',
                            height: '20px'
                          }}
                        />
                      </div>
                    </Tooltip>
                  </Box>
                ) : (
                  <Controller
                    control={control}
                    name={'issueDays' as Path<T>}
                    render={({
                      field: { onChange, value, ref },
                      fieldState: { error }
                    }) => (
                      <Box
                        display="flex"
                        gap="10px"
                        flexDirection="row"
                        alignItems="center"
                      >
                        <Box maxWidth="66px">
                          <Textfield
                            id="issue-days-input"
                            inputRef={ref}
                            placeholder="10"
                            helperText={error?.message}
                            defaultValue={
                              hasIssueDays ? days_to_issue_billing : ''
                            }
                            data-cy="issueDays"
                            error={!!error?.message}
                            inputProps={{
                              min: 0,
                              max: 180
                            }}
                            onChange={(e) => {
                              const onlyNumbers = e.target.value.replace(
                                /[^0-9]/g,
                                ''
                              )
                              onChange(onlyNumbers)
                            }}
                            value={value || ''}
                          />
                        </Box>
                        <Typography variant="smLight">
                          dias antes do vencimento
                        </Typography>
                      </Box>
                    )}
                  />
                )}
              </Stack>
            </AccordionDetails>
          </Accordion>
        </Stack>
      )}
    </Stack>
  )
}

export default AdvancedOptions
